<script setup lang="ts">
/*
 * Таблица карточек КМ (аудит × отделение).
 * GET /api/audit/reports/not-comply/ — фильтры: audits, departments, status.
 */

import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoReload } from '@/modules/onboarding/composables/useOnboardingDemoReload'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'
import { useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify/framework'

import { useAuditNonconfrormitiesStore } from '@/modules/audits/stores/useAuditNonconfrormitiesStore/useAuditNonconfrormitiesStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { ERouteNames } from '@/router/ERouteNames'
import type { IAuditNonconformitiesItem } from '../types/IAuditNonconformitiesItem'


import { VIconBtn } from 'vuetify/labs/VIconBtn'
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'
import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'

// Инициализация хука темы Vuetify
const theme = useTheme()

// Определение текущего размера экрана
const { smAndDown } = useDisplay()

const router = useRouter()
const feedbackStore = useFeedbackStore()

// Инициализация хранилища несоответствий аудитов
const store = useAuditNonconfrormitiesStore()

// Ссылки на пагинацию и сортировку (извлечение реактивных состояний из хранилища)
const { currentSortString, nextCursor } = storeToRefs(store)
const { itemsPerPage } = useTableItemsPerPage('auditNonconformities', 25)

// Синхронизация с хранилищем несоответствий
store.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  store.itemsPerPage = newVal
})

const filterConfigs: IFilterConfig[] = [
  { key: 'audits', labelPrefix: 'Аудит: ', type: 'number' },
  { key: 'standards', labelPrefix: 'Норматив: ', type: 'tree' },
  { key: 'points', labelPrefix: 'Пункт проверки: ', type: 'tree' },
  { key: 'departments', labelPrefix: 'Отделение: ', type: 'tree' },
  { key: 'managers', labelPrefix: 'Зав. отделением: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'tree' },
]

// Инициализация логики таблицы (обработка фильтров, сортировки, пагинации)
const tableLogic = useTableFilters({
  // Реактивный список текущих фильтров
  filters: computed(() => store.filters),

  // Выборки для фильтров поиска
  searchChoices: computed(() => store.searchChoices) as any,

  // Выборки для фильтров по дереву
  filterChoices: computed(() => store.filterChoices) as any,

  // Состояние загрузки фильтров
  filterLoading: store.filterLoading,

  // Метод для получения вариантов фильтров с сервера
  fetchFilterChoices: (key: string) => store.fetchFilterChoices(key),

  // Метод получения данных таблицы с сервера
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => store.fetchAudits(cursor, limit, throwOnError),
  realtimeEnabled: true,

  // Строка текущей сортировки
  currentSortString,

  // Количество элементов на странице
  itemsPerPage,

  // Метод сброса фильтров
  resetFilters: () => store.resetFilters(),

  // Курсор для следующей страницы данных
  nextCursor,

  // Количество загруженных элементов
  loadedItemsCount: computed(() => store.audits.length),

  // Общее количество элементов на сервере
  totalItemsCount: computed(() => store.totalItemsCount),

  // Карта сопоставления полей для сортировки
  sortMap: {},

  // Передача конфигурации фильтров
  filterConfigs,
})

interface IAuditNonconformityHeader {
  title: string
  key: string
  sortable?: boolean
  filterType?: string
  filterKey?: string
  align?: 'start' | 'center' | 'end'
}

// Конфигурация заголовков таблицы – строго по полям из API
const headers = computed<IAuditNonconformityHeader[]>(() => [
  { title: 'Аудит', key: 'audit_id', sortable: false, filterType: 'number', filterKey: 'audits' },
  { title: 'Тема проверки', key: 'audit_name', sortable: false },
  { title: 'Норматив', key: 'standards', sortable: false, filterType: 'tree', filterKey: 'standards' },
  { title: 'Пункты проверки', key: 'points', sortable: false, filterType: 'tree', filterKey: 'points' },
  { title: 'Отделение', key: 'department', sortable: false, filterType: 'tree', filterKey: 'departments' },
  { title: 'Зав. отделением', key: 'responsible_manager', sortable: false, filterType: 'tree', filterKey: 'managers' },
  { title: 'Отработано', key: 'worked_off_count', sortable: false },
  { title: 'Статус', key: 'status', sortable: false, filterType: 'tree', filterKey: 'status' },
])

// Вычисляемое свойство: список видимых несоответствий для текущей страницы
const displayedItems = computed<IAuditNonconformitiesItem[]>(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value

  return store.audits.slice(start, end)
})

const formatLabelList = (items?: { label: string }[]) =>
  (items || []).map(i => i.label).join(', ')

/**
 * Клик по строке открывает глобальную модалку карточки КМ.
 */
const applyAuditNumberFilter = () => {
  const inputStr = tableLogic.tempValues.value.audits || ''
  if (inputStr.trim() === '') {
    store.filters.audits = []
  } else {
    store.filters.audits = inputStr
      .split(/[,;\s]+/)
      .map((num: string) => Number.parseInt(num.trim()))
      .filter((num: number) => !isNaN(num) && num > 0)
  }
  tableLogic.filterMenus.value.audits = false
  tableLogic.triggerServerFilter()
}

const handleRowClick = async (item: IAuditNonconformitiesItem, event: Event) => {
  // Игнорируем клики по интерактивным элементам (кнопки, чипы, заголовки)
  const target = event.target as HTMLElement
  if (target.closest('.v-chip, .v-btn, button, a, .v-data-table__th')) {
    return
  }

  if (item.id) {
    await router.push({
      name: ERouteNames.AUDITS_NONCONFORMITY_DETAIL,
      params: { reportId: String(item.id) },
    })
  }
}

/**
 * Свойства строки: назначаем обработчик клика.
 */
const rowProps = (data: { item: IAuditNonconformitiesItem }) => ({
  onClick: (event: Event) => handleRowClick(data.item, event),
})

useOnboardingContext({ names: [AUDITS_CONTEXTS.nonconformities], root: () => document.body })
useOnboardingDemoReload('audit', () => store.fetchAudits())

onMounted(async () => {
  await store.fetchAudits()
})
</script>

<template>
  <div class="wrapper">
    <div>
      <!-- v-if/v-else вместо Teleport :disabled — иначе на мобилке дублируются фильтры -->
      <Teleport
        v-if="smAndDown"
        defer
        to="#audit-mobile-filters"
      >
        <VExpansionPanels class="w-100">
          <VExpansionPanel>
            <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
              <div class="d-flex align-center ga-2">
                <VIcon icon="ri-filter-3-line" size="20" />
                <span class="text-subtitle-1 font-weight-medium">Фильтры</span>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div class="d-flex flex-column ga-2 w-100">
                <VTextField
                  v-model="tableLogic.searchFilter.value"
                  class="w-100"
                  variant="outlined"
                  clearable
                  placeholder="Фильтр по ключевому слову"
                  :data-onboarding="AUDITS_ANCHORS.table.search"
                  autocomplete="off"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  type="text"
                  @keyup.enter="tableLogic.applySearchFilter()"
                />

                <VIconBtn
                  v-if="tableLogic.searchFilter.value"
                  class="w-100 rounded-sm"
                  color="primary"
                  icon="ri-filter-2-line"
                  variant="outlined"
                  :rounded="false"
                  @click="tableLogic.applySearchFilter()"
                />

                <VBtn
                  v-if="tableLogic.hasActiveFilters.value"
                  class="w-100 text-color-secondary"
                  prepend-icon="ri-delete-bin-line"
                  variant="outlined"
                  color="secondary"
                  :loading="feedbackStore.isGlobalLoading"
                  :data-onboarding="AUDITS_ANCHORS.table.resetFilters"
                  @click="tableLogic.renewDataAndResetFilters()"
                >
                  Сбросить фильтры
                </VBtn>
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </Teleport>

      <!-- Фильтры и поиск (десктоп) -->
      <div
        v-else
        class="d-flex align-center gap-2 w-100"
        :class="tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4'"
      >
        <VTextField
          v-model="tableLogic.searchFilter.value"
          class="flex-grow-0 w-25"
          variant="outlined"
          clearable
          placeholder="Фильтр по ключевому слову"
          :data-onboarding="AUDITS_ANCHORS.table.search"
          autofocus
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          type="text"
          @keyup.enter="tableLogic.applySearchFilter()"
        />

        <VIconBtn
          v-if="tableLogic.searchFilter.value"
          color="primary"
          class="rounded-sm"
          icon="ri-filter-2-line"
          variant="outlined"
          :rounded="false"
          width="48"
          height="48"
          @click="tableLogic.applySearchFilter()"
        />

        <VBtn
          v-if="tableLogic.hasActiveFilters.value"
          class="ml-auto text-color-secondary"
          prepend-icon="ri-delete-bin-line"
          variant="outlined"
          color="secondary"
          height="48"
          :loading="feedbackStore.isGlobalLoading"
          :data-onboarding="AUDITS_ANCHORS.table.resetFilters"
          @click="tableLogic.renewDataAndResetFilters()"
        >
          Сбросить фильтры
        </VBtn>
      </div>

      <!-- Чипы активных фильтров -->
      <div
        v-if="tableLogic.activeFilterChips.value.length"
        class="filter-chip d-flex ml-4 align-center gap-2 py-3"
      >
        <VChip
          v-for="chip in tableLogic.activeFilterChips.value"
          :key="`${chip.type}-${chip.value || 'no-value'}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <!-- Таблица -->
      <VDataTableServer
        :data-onboarding="AUDITS_ANCHORS.table.content"
        :items="displayedItems"
        :items-length="store.totalItemsCount"
        :headers="headers as any"
        :items-per-page="itemsPerPage"
        :row-props="rowProps"
        class="no-row-gaps custom-data-table custom-data-table-footer audit-nonconformities-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
      >
        <template #no-data>
          <div class="text-center text-italic py-4">
            Данные отсутствуют
          </div>
        </template>

        <!-- ДИНАМИЧЕСКИЕ ЗАГОЛОВКИ ФИЛЬТРОВ ИЗ COMPOSABLE -->
        <template
          v-for="col in headers.filter(h => h.filterType)"
          :key="col.key"
          #[`header.${col.key}`]="{ column }"
        >
          <div class="header-content d-flex align-center">
            <span>{{ column.title }}</span>

            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon(col.key)"
              :class="{
                'is-active': tableLogic.isSortActive(col.key),
                'is-desc': tableLogic.isSortDesc(col.key),
              }"
            />

            <!-- Спиннер -->
            <div
              v-if="tableLogic.filterLoading?.[col.filterKey!]"
              class="d-flex align-center justify-center ml-1"
              style="width: 28px; height: 28px"
            >
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>

            <VIconBtn
              v-else
              variant="plain"
              size="small"
              class="filter-icon-btn"
              :icon="tableLogic.getFilterIcon(col.filterKey!)"
              @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
            />

            <VMenu
              v-if="col.filterType === 'number'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <VCard class="pa-4">
                <VTextField
                  v-model="tableLogic.tempValues.value[col.filterKey!]"
                  autofocus
                  placeholder="Например: 123, 456, 789"
                  hide-details
                  width="220px"
                  @keyup.enter="applyAuditNumberFilter"
                />
                <div class="d-flex justify-end mt-2">
                  <VBtn
                    :class="{ 'w-100': smAndDown }"
                    size="small"
                    variant="outlined"
                    color="secondary"
                    @click="applyAuditNumberFilter"
                  >
                    Применить
                  </VBtn>
                </div>
              </VCard>
            </VMenu>

            <VMenu
              v-else
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                :field-key="col.filterKey!"
                :store="store"
                :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                @apply="tableLogic.applyFilter(col.filterKey!)"
              />
            </VMenu>
          </div>
        </template>
        <!-- КОНЕЦ ДИНАМИЧЕСКИХ ЗАГОЛОВКОВ -->

        <!-- DATA: Аудит -->
        <template #[`item.audit_id`]="{ item }">
          <span>{{ item.audit_id }}</span>
        </template>

        <template #[`item.audit_name`]="{ item }">
          <CollapsibleText
            v-if="item.audit_name"
            :text="item.audit_name"
          />
          <span v-else />
        </template>

        <template #[`item.standards`]="{ item }">
          <CollapsibleText
            v-if="item.standards?.length"
            :text="formatLabelList(item.standards)"
          />
          <span v-else />
        </template>

        <template #[`item.points`]="{ item }">
          <CollapsibleText
            v-if="item.points?.length"
            :text="formatLabelList(item.points)"
          />
          <span v-else />
        </template>

        <template #[`item.department`]="{ item }">
          <span>{{ item.department?.name || '' }}</span>
        </template>

        <template #[`item.responsible_manager`]="{ item }">
          <ShortFio
            :person="item.responsible_manager"
            empty-text=""
          />
        </template>

        <template #[`item.worked_off_count`]="{ item }">
          <span>{{ item.worked_off_count }}/{{ item.findings_count }}</span>
        </template>

        <template #[`item.status`]="{ item }">
          <VChip
            class="status-badge"
            :class="item.status"
            size="small"
          >
            {{ item.status_label || item.status }}
          </VChip>
        </template>

        <template #bottom>
          <div
            class="custom-data-table-footer"
            :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
          >
            <VDataTableFooter
              :show-current-page="true"
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>
  <RouterView />
</template>

<style scoped lang="scss">
/* Стили ПОЛНОСТЬЮ скопированы из AuditRegisterTable.vue */
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
  white-space: nowrap;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
  padding-block: 0.75rem !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr:hover) {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button,
    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

/* Ширины колонок nonconformities: компактные для коротких данных, расширенные для длинных текстов */
:deep(.audit-nonconformities-table th:nth-child(1)),
:deep(.audit-nonconformities-table td:nth-child(1)) {
  width: 5rem !important;
  min-width: 5rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(2)),
:deep(.audit-nonconformities-table td:nth-child(2)) {
  width: 14rem !important;
  min-width: 12rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(3)),
:deep(.audit-nonconformities-table td:nth-child(3)) {
  width: 10rem !important;
  min-width: 9rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(4)),
:deep(.audit-nonconformities-table td:nth-child(4)) {
  min-width: 25rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(5)),
:deep(.audit-nonconformities-table td:nth-child(5)) {
  width: 14rem !important;
  min-width: 12rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(6)),
:deep(.audit-nonconformities-table td:nth-child(6)) {
  width: 11rem !important;
  min-width: 10rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(7)),
:deep(.audit-nonconformities-table td:nth-child(7)) {
  width: 6.5rem !important;
  min-width: 6.5rem !important;
}

:deep(.audit-nonconformities-table th:nth-child(8)),
:deep(.audit-nonconformities-table td:nth-child(8)) {
  width: 9rem !important;
  min-width: 9rem !important;
}

.status-badge {
  font-weight: 500;
  padding-inline: 6px;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}
</style>
